import { useEffect, useState, useMemo } from 'react'
import Link from 'next/link'
import Loader from '@byma/shared/components/Loader'
import Router, { useRouter } from 'next/router'
import { Virtuoso } from 'react-virtuoso'
import dayjs from 'dayjs'
import useAuth from '@byma/shared/hooks/useAuth'
import fetcher from '@byma/shared/helpers/fetcher'
import useFilters from '@byma/shared/hooks/useFilters'
import SearchInput from '@byma/shared/components/SearchInput'
import CouponFilter from '@byma/shared/components/CouponFilter'
import DateFilter from '@byma/shared/components/DateFilter'
import EventFilter from '@byma/shared/components/EventFilter'
import ChannelFilter from '@byma/shared/components/ChannelFilter'
import StatusFilter from '@byma/shared/components/StatusFilter'
import QRCodeScannerButton from 'components/QRCodeScannerButton'
import useSWRInfinite from 'swr/infinite'

export const statusParser = {
    paid: 'Pago',
    pending: 'Pendente',
    canceled: 'Cancelado',
    under_analysis: 'Sob análise',
    // Custom parser so rename is possible
    refunded: 'Estornado',
    expired: 'Expirado',
    refused: 'Não autorizado',
    errored: 'Erro',
    'order placed': 'Pendente',
    POS: 'Pago'
}

function getCloudinaryURL(url,height) {
    if (typeof url === 'undefined' || !url) return ''
    return `https://res.cloudinary.com/htkavmx5a/image/upload/c_scale,f_auto,h_${height},q_auto/${url.split('/').pop()}`
}

function toQueryString(obj, forceKey=null) {
    return Object.keys(obj).map(function (key) {
        if (typeof obj[key] === 'object') {
            return toQueryString(obj[key], key)
        }
        return (forceKey || key) + '=' + obj[key]
    }).join('&');
}

export default function TicketsTable() {
    const session = useAuth()
    const { token, user: currentUser } = session
    let { isReady, query, ...router } = useRouter()
    const { originalQueryString, parsedFilters, rawFilters } = useFilters(query, () => true)
    const [isExporting, setIsExporting] = useState(false)

    function getKey(pageIndex, previousPageData) {
        if (!token) return null
        if (previousPageData?.result?.length === 0) return null

        if (pageIndex === 0) {
            return [`/api/tickets/getAll?${toQueryString({...parsedFilters, perPage: 50})}`, null, token]
        }

        const lastResult = previousPageData?.result[previousPageData.result.length - 1]
        if (!lastResult) return null

        return [`/api/tickets/getAll?${toQueryString({...parsedFilters, toDate: lastResult?.dateBought, perPage: 50})}`, null, token]
    }

    const { data: tickets, isValidating, size, setSize } = useSWRInfinite(getKey, fetcher, { revalidateOnFocus: false, revalidateFirstPage: false })

    function handleCSVExport() {
        setIsExporting(true)
        return fetch(`/api/tickets/export?${toQueryString({ ...parsedFilters })}`, {
            headers: {
                'Content-Type': 'application/json',
                Auth: token,
            }
        })
            .then(req => req.json())
            .then(res => {
                let link = document.createElement('a')
                link.href = res.url
                link.click()
            })
            .finally(() => setIsExporting(false))
    }

    const tableData = useMemo(() => {
        if (!tickets) {
            return []
        }

        return tickets.reduce((acc, cur) => {
            return [...acc, ...(cur?.result || [])]
        }, [])
    }, [tickets])

    function rowRenderer(index, data) {
        return (
            <div
                key={index}
                className="px-3"
            >
                <Link href={`/tickets/${tableData[index]._id}?${originalQueryString}`}>
                    <a>
                        <TicketItem {...{ ticket: tableData[index] || {} }} />
                    </a>
                </Link>
            </div>
        )
    }

    return (
        <>
            <div className="flex justify-between items-center">
                <div className="flex space-x-3 p-3 shadow w-full overflow-y-auto">
                    <SearchInput currentQuery={rawFilters.search} />
                    <QRCodeScannerButton />
                    <DateFilter activeKey={rawFilters.dateKey} />
                    <EventFilter token={token} filteredEvents={rawFilters.parties} />
                    <ChannelFilter token={token} filteredChannels={rawFilters.channels} />
                    <CouponFilter token={token} filteredCoupons={rawFilters.coupons} />
                    <StatusFilter filteredStatus={rawFilters.status} />
                    <button onClick={handleCSVExport} className="flex justify-between items-center cursor-pointer rounded shadow px-2 py-1 text-sm hover:shadow-md whitespace-nowrap focus:shadow-sm">
                        { isExporting && (
                            <div className="w-5 h-5">
                                <Loader className="w-5 h-5 mr-2" />
                            </div>
                        )}
                        { !isExporting && (
                            <>
                                <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" viewBox="0 0 20 20" fill="currentColor">
                                    <path fillRule="evenodd" d="M3 17a1 1 0 011-1h12a1 1 0 110 2H4a1 1 0 01-1-1zm3.293-7.707a1 1 0 011.414 0L9 10.586V3a1 1 0 112 0v7.586l1.293-1.293a1 1 0 111.414 1.414l-3 3a1 1 0 01-1.414 0l-3-3a1 1 0 010-1.414z" clipRule="evenodd" />
                                </svg>
                                <p>
                                    Exportar para CSV
                                </p>
                            </>
                        )}
                    </button>
                </div>
            </div>
            <Virtuoso
                data={tableData}
                endReached={() => setSize(size + 1)}
                overscan={200}
                itemContent={rowRenderer}
                components={{
                    Header: () => {
                        return (
                            <div
                                className="h-2"
                            />
                        )
                    },
                    Footer: () => {
                        return (
                            <div
                                className="p-3 flex items-center justify-center"
                            >
                                { isValidating ? (
                                    <Loader /> 
                                ): (
                                    <p className="text-sm text-gray-700">
                                        { tableData?.length === 0 ? 'Nenhum resultado encontrado' : 'Fim dos resultados' }
                                    </p>
                                )}
                            </div>
                        )
                    }
                }}
            />
        </>
    )
}

function TicketItem ({ ticket, active = false }) {
    return (
        <div className={`flex justify-between items-center cursor-pointer box box-hover ${active && 'box-active'}`}>
            <div className="flex flex-col w-64">
                <p className={`mb-2 w-min label whitespace-nowrap text-center ${ticket.status !== 'paid' && ticket.status !== 'POS' ? '' : 'success'} text-sm`}>{statusParser[ticket.status]}</p>
                <p className="font-bold">{ticket.guestInfo?.name || 'N/A'}</p>
                <p className="text-md">{ticket.guestInfo?.rg || 'N/A'}</p>
            </div>
            <div className="flex flex-col w-full text-right items-end">
                <p className="text-gray-700">{dayjs(ticket.dateBought).format('DD/MM/YYYY HH:mm')}</p>
                <p className="text-md font-bold truncate">
                    {ticket.ticketType?.name}
                </p>
                <div className="flex space-x-1 items-center">
                    {ticket?.ticketType?.partyId?.pictures?.photos[0] ? (
                        <div className="rounded-full w-4 h-4 min-w-0">
                            <img src={getCloudinaryURL(ticket.ticketType.partyId.pictures.photos[0], 60)} alt={ticket.ticketType.partyId.name} className="rounded-full w-4 h-4 object-cover" />
                        </div>
                    ) : (
                        <div className="w-4 h-4 rounded-full bg-gray-400 overflow-hidden min-w-0" />
                    )}
                    <p className="text-md truncate">
                        {ticket.ticketType?.partyId?.name}
                    </p>
                </div>
                <div className="flex space-x-1 items-center">
                    {ticket?.owner?.profile?.picture ? (
                        <div className="rounded-full w-4 h-4 min-w-0">
                            <img src={ticket.owner.profile.picture} alt={""} className="rounded-full w-4 h-4 object-cover" />
                        </div>
                    ) : (
                        <div className="w-4 h-4 rounded-full bg-gray-400 overflow-hidden min-w-0" />
                    )}
                    <p className="text-md text-gray-700 truncate">
                        {ticket.owner?.profile?.name || 'Usuário não associado'}
                    </p>
                </div>
            </div>
        </div>
    )
}
